"use client";

import {
  ComponentPropsWithoutRef,
  FC,
  ReactElement,
  useRef,
  useState,
} from "react";
import PresentationCard from "../PresentationCard";
import Typography from "../Typography";
import { MediaBlockData } from "./MediaBlock";

export type FeaturesBlockData = {
  title?: string;
  description?: string;
  items: {
    title: string;
    description: string;
    media: MediaBlockData;
    icon: ReactElement<HTMLDivElement>;
  }[];
};
type FeaturesBlockProps = {
  data: FeaturesBlockData;
} & ComponentPropsWithoutRef<"section">;

const FeatureBlock: FC<FeaturesBlockProps> = ({ data, ...rest }) => {
  const [activeFeature, setActiveFeature] = useState<number>(0);
  const [playingVideoIndex, setPlayingVideoIndex] = useState<number | null>(
    null,
  );

  // Same lazy-load contract as `PresentationCard`: every `<video>` carries
  // `preload="metadata"` so the browser fetches just enough bytes
  // (~few hundred KB, lazy after the page is interactive) to render the
  // first frame as a built-in poster — which keeps the section from looking
  // like an empty grey rectangle before the user engages, while still NOT
  // streaming the full video like the original `autoPlay` setup did. When
  // a row is engaged (hover or click) we `play()` the matching video and
  // pause+rewind the others so we never decode more than one stream at a
  // time.
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  const activateFeature = (index: number) => {
    setActiveFeature(index);
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === index) {
        void video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  };

  return (
    <section {...rest} className={`${rest.className}`}>
      <Typography variant="h2" className="text-center">
        {data.title ? data.title : "**Save hours** using AI"}
      </Typography>

      <Typography
        variant="body"
        className="text-slate-500 mt-8 max-w-2xl text-center mx-auto"
      >
        {data.description
          ? data.description
          : "Never spend hours editing your videos again. Klap uses AI to automatically create engaging clips from your content."}
      </Typography>

      <div className="flex-col md:flex-row gap-3 mt-16 hidden md:flex">
        <div className="flex-1 flex flex-col gap-3 text-left max-w-lg h-full ">
          {data.items.map((feature, index) => (
            <div
              onClick={() => activateFeature(index)}
              onMouseEnter={() => activateFeature(index)}
              className={`transition-opacity cursor-pointer ${
                activeFeature === index ? "bg-slate-100 " : "hover:bg-slate-100"
              } p-6 rounded-2xl ${
                activeFeature === index ? "opacity-100" : ""
              }`}
              key={feature.title}
            >
              <div className="flex items-center gap-2">
                {/* The icon comes from the consuming page's data prop. We
                    used to `cloneElement(feature.icon, { className: … })`
                    here, but that pattern stopped working in App Router:
                    icons declared in a Server Component module (the
                    consumer's data file) are pre-rendered to React
                    elements that `cloneElement` can no longer mutate
                    across the server/client boundary. Sizing it via a
                    wrapper achieves the same visual result without that
                    constraint. */}
                <span className="w-5 h-5 shrink-0 text-slate-40 [&>svg]:w-full [&>svg]:h-full">
                  {feature.icon}
                </span>
                <Typography variant={"subtitle1"} className="flex">
                  {feature.title}
                </Typography>
              </div>
              <Typography
                variant={"body2"}
                className="mt-1 mx-7 text-slate-500"
              >
                {feature.description}
              </Typography>
            </div>
          ))}
        </div>
        <div className="flex-1">
          <div className="isolate overflow-hidden rounded-2xl sticky top-32 pt-10 px-10 bg-slate-100">
            <div className="relative">
              {data.items.map((feature, index) => (
                <video
                  ref={(el) => {
                    videoRefs.current[index] = el;
                  }}
                  playsInline
                  loop
                  muted
                  preload="metadata"
                  disableRemotePlayback
                  src={feature.media.src}
                  key={feature.title}
                  className={`transition-opacity rounded-t-lg ${
                    index === 0 ? "" : "absolute inset-0"
                  } ${activeFeature === index ? "opacity-100" : "opacity-0"}`}
                >
                  <track
                    kind="captions"
                    srcLang="en"
                    label="English"
                    src="/captions/empty.vtt"
                    default
                  />
                </video>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-12 md:hidden mt-12">
        {data.items.map((feature, index) => (
          <PresentationCard
            title={feature.title}
            description={feature.description}
            index={index}
            playingVideoIndex={playingVideoIndex}
            setPlayingVideoIndex={(i) => setPlayingVideoIndex(i)}
            key={feature.title}
            media={feature.media}
          />
        ))}
      </div>
    </section>
  );
};

export default FeatureBlock;
